/* =============================================================================
 *  VALBLOCK — Scripts du thème
 * ========================================================================== */

/* Import direct d'AOS depuis node_modules : le module est intégré au bundle
 * `assets/js/app.js`, sa feuille de style l'est à `assets/css/styles.css`
 * (voir `ressources/sass/styles.scss`). */
import AOS from 'aos';

(function () {
    'use strict';

    /* -------------------------------------------------------------------------
     *  Thème verrouillé en clair
     *  Le sélecteur a été retiré de la navbar : plus rien ne permet de sortir
     *  du mode sombre. Or le script d'Azuriom repose `data-bs-theme` depuis le
     *  localStorage, et il est différé — il s'exécute donc APRÈS l'attribut
     *  écrit dans le gabarit. On reconduit la valeur ici, sur DOMContentLoaded,
     *  qui vient après tous les scripts différés.
     *
     *  Le SCSS conserve son bloc `[data-bs-theme="dark"]` : si le sélecteur
     *  revient un jour, il n'y a que cette fonction à retirer.
     * ---------------------------------------------------------------------- */
    function lockLightTheme() {
        if (document.documentElement.getAttribute('data-bs-theme') !== 'light') {
            document.documentElement.setAttribute('data-bs-theme', 'light');
        }
    }

    /* -------------------------------------------------------------------------
     *  Animations au défilement
     *  Volontairement sobres : un fondu vers le haut de quelques pixels, une
     *  seule fois, et rien du tout si le visiteur a demandé à réduire les
     *  animations.
     *
     *  /!\ La feuille d'AOS met tout élément `[data-aos]` à `opacity: 0`. Si
     *  l'initialisation échoue, le contenu resterait invisible : on retire donc
     *  les attributs dans ce cas.
     * ---------------------------------------------------------------------- */
    function stripAos() {
        document.querySelectorAll('[data-aos]').forEach(function (node) {
            node.removeAttribute('data-aos');
        });
    }

    function initAos() {
        var reduced = window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduced || typeof AOS === 'undefined') {
            stripAos();
            return;
        }

        try {
            AOS.init({
                duration: 600,
                easing: 'ease-out-cubic',
                offset: 60,
                delay: 0,
                once: true,
                mirror: false,
                anchorPlacement: 'top-bottom',
            });
        } catch (error) {
            stripAos();
        }
    }

    /* -------------------------------------------------------------------------
     *  Copie de l'adresse du serveur
     *  Utilise l'API Clipboard quand elle est disponible (elle exige HTTPS ou
     *  localhost), sinon retombe sur un <textarea> hors écran.
     * ---------------------------------------------------------------------- */
    function copyText(text) {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        }

        return new Promise(function (resolve, reject) {
            var area = document.createElement('textarea');
            area.value = text;
            area.setAttribute('readonly', '');
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.appendChild(area);
            area.select();

            try {
                document.execCommand('copy') ? resolve() : reject();
            } catch (error) {
                reject(error);
            } finally {
                document.body.removeChild(area);
            }
        });
    }

    function bindCopy() {
        document.querySelectorAll('[data-copy]').forEach(function (button) {
            var valueNode = button.querySelector('.vb-ip__value');
            var original = valueNode ? valueNode.textContent : null;
            var timer = null;

            button.addEventListener('click', function () {
                copyText(button.dataset.copy).then(function () {
                    button.classList.add('is-copied');

                    if (valueNode && button.dataset.copiedLabel) {
                        valueNode.textContent = button.dataset.copiedLabel;
                    }

                    window.clearTimeout(timer);
                    timer = window.setTimeout(function () {
                        button.classList.remove('is-copied');
                        if (valueNode && original !== null) {
                            valueNode.textContent = original;
                        }
                    }, 1800);
                }).catch(function () {
                    /* Copie refusée par le navigateur : on ne casse rien. */
                });
            });
        });
    }

    /* -------------------------------------------------------------------------
     *  Tableaux du contenu rédigé : conteneur de défilement
     *  Un tableau large déborde et pousse toute la page — surtout en mobile.
     *  Le HTML vient de l'éditeur d'Azuriom, on ne peut donc pas écrire le
     *  conteneur dans le gabarit : on l'ajoute ici.
     * ---------------------------------------------------------------------- */
    function wrapTables(root) {
        (root || document).querySelectorAll('.vb-prose table, .content table').forEach(function (table) {
            if (table.closest('.vb-table-wrap')) return;

            var wrap = document.createElement('div');
            wrap.className = 'vb-table-wrap';
            // `tabindex` rend la zone atteignable au clavier : sans lui, une
            // personne qui n'utilise pas la souris ne peut pas la faire défiler.
            wrap.tabIndex = 0;
            wrap.setAttribute('role', 'region');

            var caption = table.querySelector('caption');
            wrap.setAttribute('aria-label', (caption && caption.textContent.trim()) || table.dataset.label || 'Tableau');

            table.parentNode.insertBefore(wrap, table);
            wrap.appendChild(table);
        });
    }

    /* -------------------------------------------------------------------------
     *  Lecture d'une vidéo sur place
     *  La vignette n'est qu'une façade : au clic, elle est remplacée par le
     *  lecteur, dans la page. Rien de YouTube n'est chargé avant — ni script,
     *  ni cookie — ce qui évite le coût d'une iframe sur chaque visite.
     *
     *  Le déclencheur est un <button> : une fois la vidéo lancée, il est
     *  remplacé par un <div> portant les mêmes classes. Laisser une iframe
     *  dans un bouton donnerait un élément interactif imbriqué dans un autre.
     * ---------------------------------------------------------------------- */
    function bindInlineVideos() {
        document.addEventListener('click', function (event) {
            var trigger = event.target.closest('[data-video-embed]');
            if (!trigger) return;

            event.preventDefault();

            var frame = trigger.querySelector('.vb-influencers__video-frame');
            if (!frame) return;

            var iframe = document.createElement('iframe');
            iframe.src = trigger.dataset.videoEmbed;
            iframe.title = trigger.dataset.videoTitle || '';
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            iframe.setAttribute('frameborder', '0');

            frame.replaceChildren(iframe);

            var player = document.createElement('div');
            player.className = trigger.className + ' is-playing';
            player.appendChild(frame);

            trigger.replaceWith(player);
        });
    }

    /* -------------------------------------------------------------------------
     *  Sélecteur de quantité de la boutique
     *  La fiche d'un article est injectée en AJAX dans la modale : on écoute
     *  donc au niveau du document plutôt que sur les boutons, qui n'existent
     *  pas encore au chargement.
     *
     *  Le champ reste un `<input name="quantity">` : les deux boutons ne font
     *  que le piloter, et le libellé du bouton d'achat suit la valeur.
     * ---------------------------------------------------------------------- */
    function syncStepper(root) {
        var input = root.querySelector('.vb-stepper__input');
        var output = root.querySelector('[data-stepper-output]');
        if (!input) return;

        var min = parseInt(input.min || '1', 10);
        var max = parseInt(input.max || '0', 10) || Infinity;
        var value = Math.min(Math.max(parseInt(input.value || min, 10) || min, min), max);

        input.value = value;

        if (output && root.dataset.stepperLabel) {
            output.textContent = root.dataset.stepperLabel.replace('%s', value);
        }

        root.querySelectorAll('[data-stepper-step]').forEach(function (button) {
            var step = parseInt(button.dataset.stepperStep, 10);
            button.disabled = (step < 0 && value <= min) || (step > 0 && value >= max);
        });
    }

    function bindSteppers() {
        document.addEventListener('click', function (event) {
            var button = event.target.closest('[data-stepper-step]');
            if (!button) return;

            var root = button.closest('[data-stepper]');
            var input = root && root.querySelector('.vb-stepper__input');
            if (!input) return;

            event.preventDefault();
            input.value = (parseInt(input.value || '1', 10) || 1) + parseInt(button.dataset.stepperStep, 10);
            syncStepper(root);
        });

        document.addEventListener('input', function (event) {
            var input = event.target.closest('.vb-stepper__input');
            if (input) syncStepper(input.closest('[data-stepper]'));
        });

        // La modale est remplie après coup : on recale au moment où elle s'ouvre.
        document.addEventListener('shown.bs.modal', function (event) {
            event.target.querySelectorAll('[data-stepper]').forEach(syncStepper);
            wrapTables(event.target);
        });

        document.querySelectorAll('[data-stepper]').forEach(syncStepper);
    }

    /* -------------------------------------------------------------------------
     *  Infobulles Bootstrap (réseaux sociaux du pied de page)
     * ---------------------------------------------------------------------- */
    function bindTooltips() {
        if (typeof window.bootstrap === 'undefined') {
            return;
        }

        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(function (node) {
            window.bootstrap.Tooltip.getOrCreateInstance(node);
        });
    }

    document.addEventListener('DOMContentLoaded', function () {
        lockLightTheme();
        bindCopy();
        bindTooltips();
        bindInlineVideos();
        bindSteppers();
        wrapTables();
        initAos();
    });
})();


/* -----------------------------------------------------------------------------
 *  Carrousel de statistiques du hero
 *  Les éléments se superposent dans la même cellule de grille ; on ne fait que
 *  déplacer la classe `is-active`. Rien n'est masqué si le script ne tourne
 *  pas : la première stat porte déjà la classe côté serveur.
 * -------------------------------------------------------------------------- */
(function () {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-vb-carousel]').forEach(function (root) {
        var items = root.querySelectorAll('[data-vb-carousel-item]');
        if (items.length < 2 || reduced) return;

        var delay = parseInt(root.dataset.vbCarouselDelay || '5000', 10);
        var index = 0;
        var timer = null;

        function show(next) {
            items[index].classList.remove('is-active');
            index = (next + items.length) % items.length;
            items[index].classList.add('is-active');
        }

        function start() { timer = window.setInterval(function () { show(index + 1); }, delay); }
        function stop() { window.clearInterval(timer); timer = null; }

        // On ne fait pas défiler une carte que personne ne regarde : c'est
        // autant de reflows évités quand l'onglet est en arrière-plan.
        document.addEventListener('visibilitychange', function () {
            document.hidden ? stop() : (timer || start());
        });

        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', function () { if (!timer) start(); });

        start();
    });
})();
